import { DocumentNode, useApolloClient } from '@apollo/client'
import { getOperationName } from '@apollo/client/utilities'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import { useEffect, useRef } from 'react'
import {
  KPI_LOCK_DRAFT_UNREADABLE,
  KPI_LOCK_UNAVAILABLE,
  KPI_SUBJECT_LOCKED,
  WaveLockRefusal,
  onWaveLockRefusal
} from '../../../apollo/waveLockLink'
import { QUESTIONNAIRE } from '../../../data/gql/questionnaire/queries'
import { WAVES } from '../../../modules/Project/Project.query'
import { resetRefusedEntry } from '../../../modules/Questionnaire/GenericEntry/refusedEntryResets'
import {
  KPI_LOCK_DRAFT_UNREADABLE_MESSAGE,
  KPI_LOCK_UNAVAILABLE_MESSAGE,
  KPI_SUBJECT_LOCKED_MESSAGE
} from '../../../modules/Questionnaire/Kpi/kpiErrorMessage'
import { useWaveId } from './useWaveId'

const LOCK_UNAVAILABLE_MESSAGES: Partial<Record<WaveLockRefusal, string>> = {
  [KPI_LOCK_UNAVAILABLE]: KPI_LOCK_UNAVAILABLE_MESSAGE,
  [KPI_LOCK_DRAFT_UNREADABLE]: KPI_LOCK_DRAFT_UNREADABLE_MESSAGE
}

/** A save the wave's lock refused: reload the waves if it launched and, if the save was the open wave's, reload its questionnaire and, for a launch, put the refused question back as saved. */
export const useWaveLockRefusals = () => {
  const client = useApolloClient()
  const { enqueueSnackbar } = useSnackbar()
  const waveId = useWaveId()
  // A bulk save can be refused once per field; one reload answers them all.
  const inFlight = useRef(new Map<string, Promise<void>>())

  useEffect(() => {
    // By name: a cache gc gives a document a new transformed copy, which refetchQueries no longer matches.
    const refetch = (query: DocumentNode) => {
      const name = getOperationName(query) ?? ''
      const pending = inFlight.current.get(name)
      if (pending) {
        return pending
      }
      const done = () => {
        inFlight.current.delete(name)
      }
      const reload = client.refetchQueries({ include: [name] }).then(done, done)
      inFlight.current.set(name, reload)
      return reload
    }

    return onWaveLockRefusal((refusal, refusedWaveId, refusedEntryId) => {
      const unavailable = LOCK_UNAVAILABLE_MESSAGES[refusal]
      if (!unavailable) {
        refetch(WAVES)
      }
      if (refusedWaveId && refusedWaveId !== waveId) {
        return
      }
      if (unavailable) {
        enqueueSnackbar(unavailable, { variant: SnackbarVariant.Error })
        refetch(QUESTIONNAIRE)
        return
      }
      if (refusal === KPI_SUBJECT_LOCKED) {
        enqueueSnackbar(KPI_SUBJECT_LOCKED_MESSAGE, {
          variant: SnackbarVariant.Error
        })
        refetch(QUESTIONNAIRE).then(() => {
          if (refusedEntryId) {
            resetRefusedEntry(refusedEntryId)
          }
        })
      }
    })
  }, [client, enqueueSnackbar, waveId])
}
